// "Gestion des Attestations" — staff screen: paginated lists per tab (toutes,
// validation finance, validation académique, approuvées & documents), manual
// validation with the "étudiant ajourné" safeguard, physical workflow
// (télécharger → imprimée → délivrée) and the per-institution configuration.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileText,
  Download,
  CheckCircle,
  XCircle,
  Calendar,
  User,
  Search,
  DollarSign,
  GraduationCap,
  Clock,
  AlertTriangle,
  Loader2,
  Settings,
  Printer,
} from 'lucide-react';
import {
  getPendingFinanceRequests,
  getPendingAcademicRequests,
  validateFinance,
  validateAcademic,
  getAllRequests,
  getApprovedRequests,
  getPendingOtherYearsCount,
  markAsReadyToCollect,
  downloadForPrint,
  markAsPrinted,
  markAsDelivered,
} from '../../../services/studentRequestsService';
import {
  StudentRequest,
  RequestStatus,
  AttestationType,
  PaginatedAdminRequests,
  PaginationMeta,
} from '../../../types/student-requests.types';
import { Permission } from '../../../types/global.types';
import { usePermissions } from '../../auth/hooks/usePermissions';
import ConfirmDialog from '../../../shared/components/ConfirmDialog';
import { CuteConfirmModal } from '../../../shared/components/CuteConfirmModal';
import { fetchCurrentAcademicYear, getAcademicYears, type AcademicYear } from '../../../services/academicPlanningService';
import { getTypeLabel, getStatusBadge } from '../components/requests/requestLabels';
import { RequestsPagination } from '../components/requests/RequestsPagination';
import { AcademicAttentionBadge, attentionDecisionLabel } from '../components/requests/AcademicAttentionBadge';
import { PendingOtherYearsBanner } from '../components/requests/PendingOtherYearsBanner';
import { AttestationConfigModal } from '../components/requests/AttestationConfigModal';

type TabType = 'all' | 'pending-finance' | 'pending-academic' | 'approved';
type ValidationSide = 'finance' | 'academic';

const PAGE_SIZE = 20;
const EMPTY_LIST: PaginatedAdminRequests = { data: [], meta: { page: 1, limit: PAGE_SIZE, total: 0 } };
const TABS: TabType[] = ['all', 'pending-finance', 'pending-academic', 'approved'];

type ListsState = Record<TabType, PaginatedAdminRequests>;
type PagesState = Record<TabType, number>;

const apiErrorMessage = (error: unknown, fallback: string): string => {
  const maybe = error as { response?: { data?: { message?: string | string[] } } };
  const message = maybe.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message ?? fallback;
};

// Toast notification for errors/success
const Toast = ({ message, type, onClose }: { message: string; type: 'error' | 'success'; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-6 right-6 z-[60] animate-[slideIn_300ms_ease-out]">
      <div className={`flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg border ${
        type === 'error'
          ? 'bg-red-50 border-red-200 text-red-800'
          : 'bg-green-50 border-green-200 text-green-800'
      }`}>
        {type === 'error'
          ? <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
          : <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
        }
        <p className="text-sm font-medium">{message}</p>
        <button onClick={onClose} className="ml-2 text-gray-400 hover:text-gray-600">
          <XCircle className="w-4 h-4" />
        </button>
      </div>
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

const RequestManagementPage = () => {
  const [searchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const canManageRequests = hasPermission(Permission.REQUEST_MANAGE);
  const canValidateFinance = hasPermission(Permission.REQUEST_VALIDATE_FINANCE);
  const canValidateAcademic = hasPermission(Permission.REQUEST_VALIDATE_ACADEMIC);
  const canConfigure = hasPermission(Permission.REQUEST_CONFIG);

  const visibleTabs = useMemo(
    () =>
      TABS.filter(
        (tab) =>
          (tab !== 'pending-finance' || canValidateFinance) &&
          (tab !== 'pending-academic' || canValidateAcademic),
      ),
    [canValidateFinance, canValidateAcademic],
  );

  // Read initial tab from URL params
  const requestedTab = (searchParams.get('tab') as TabType) || 'all';
  const [activeTab, setActiveTab] = useState<TabType>(requestedTab);
  const [lists, setLists] = useState<ListsState>({
    all: EMPTY_LIST,
    'pending-finance': EMPTY_LIST,
    'pending-academic': EMPTY_LIST,
    approved: EMPTY_LIST,
  });
  const [pages, setPages] = useState<PagesState>({ all: 1, 'pending-finance': 1, 'pending-academic': 1, approved: 1 });
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [validatingId, setValidatingId] = useState<string | null>(null);
  const [includeReadyToCollect, setIncludeReadyToCollect] = useState(true);
  const [includeDelivered, setIncludeDelivered] = useState(false);
  // Year selector: defaults to the year in progress (calendar-resolved by the
  // backend); '' = every year. null until the years have loaded.
  const [years, setYears] = useState<AcademicYear[]>([]);
  const [currentYearId, setCurrentYearId] = useState<string | null>(null);
  const [yearFilter, setYearFilter] = useState<string | null>(null);
  const [pendingOtherYears, setPendingOtherYears] = useState(0);
  const [configOpen, setConfigOpen] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ message: string; type: 'error' | 'success' } | null>(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    message: string;
    confirmText: string;
    variant: 'success' | 'danger' | 'warning' | 'info' | 'ready' | 'print' | 'deliver';
    onConfirm: () => Promise<void>;
  } | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // "Étudiant ajourné" safeguard before approving a réussite
  const [attentionConfirm, setAttentionConfirm] = useState<{ request: StudentRequest; side: ValidationSide } | null>(null);

  // Rejection modal state
  const [rejectionModal, setRejectionModal] = useState<{
    requestId: string;
    type: ValidationSide;
    studentName: string;
  } | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  // Delivery modal state
  const [deliveryModal, setDeliveryModal] = useState<{ request: StudentRequest } | null>(null);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [deliverLoading, setDeliverLoading] = useState(false);

  const showError = useCallback((msg: string) => setToast({ message: msg, type: 'error' }), []);
  const showSuccess = useCallback((msg: string) => setToast({ message: msg, type: 'success' }), []);

  // A tab the user may not see falls back to "Toutes les demandes".
  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) setActiveTab('all');
  }, [visibleTabs, activeTab]);

  useEffect(() => {
    let cancelled = false;
    const loadYears = async (): Promise<void> => {
      const [list, current] = await Promise.all([
        getAcademicYears().catch(() => [] as AcademicYear[]),
        fetchCurrentAcademicYear().catch(() => null),
      ]);
      if (cancelled) return;
      setYears(list);
      setCurrentYearId(current?.id ?? null);
      setYearFilter(current?.id ?? '');
    };
    void loadYears();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchTab = useCallback(
    async (tab: TabType, page: number, academicYearId?: string): Promise<PaginatedAdminRequests> => {
      const params = { academicYearId, page, limit: PAGE_SIZE };
      switch (tab) {
        case 'all':
          return getAllRequests(params);
        case 'pending-finance':
          return canValidateFinance ? getPendingFinanceRequests(params) : EMPTY_LIST;
        case 'pending-academic':
          return canValidateAcademic ? getPendingAcademicRequests(params) : EMPTY_LIST;
        case 'approved':
          return getApprovedRequests(params);
        default:
          return EMPTY_LIST;
      }
    },
    [canValidateFinance, canValidateAcademic],
  );

  /** Reloads every tab (keeps each tab's page) — after any action or year change. */
  const loadAllRequests = useCallback(
    async (pageOverrides?: Partial<PagesState>): Promise<void> => {
      if (yearFilter === null) return;
      const academicYearId = yearFilter || undefined;
      const effectivePages: PagesState = { ...pages, ...pageOverrides };
      try {
        setLoading(true);
        const results = await Promise.allSettled(
          TABS.map((tab) => fetchTab(tab, effectivePages[tab], academicYearId)),
        );
        const next = {} as ListsState;
        results.forEach((result, index) => {
          next[TABS[index]] = result.status === 'fulfilled' ? result.value : EMPTY_LIST;
        });
        setLists(next);
        if (results.every((r) => r.status === 'rejected')) {
          showError('Erreur lors du chargement des demandes');
        }
      } catch (error) {
        showError(apiErrorMessage(error, 'Erreur lors du chargement des demandes'));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [yearFilter, pages, fetchTab, showError],
  );

  // Year change: every tab restarts at page 1; the banner counts the other years.
  useEffect(() => {
    if (yearFilter === null) return;
    const firstPages: PagesState = { all: 1, 'pending-finance': 1, 'pending-academic': 1, approved: 1 };
    setPages(firstPages);
    void loadAllRequests(firstPages);

    if (yearFilter) {
      getPendingOtherYearsCount(yearFilter)
        .then((r) => setPendingOtherYears(r.count))
        .catch(() => setPendingOtherYears(0));
    } else {
      setPendingOtherYears(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearFilter, canValidateFinance, canValidateAcademic]);

  const changePage = async (tab: TabType, page: number): Promise<void> => {
    const nextPages = { ...pages, [tab]: page };
    setPages(nextPages);
    try {
      setTabLoading(true);
      const result = await fetchTab(tab, page, yearFilter || undefined);
      setLists((prev) => ({ ...prev, [tab]: result }));
    } catch (error) {
      showError(apiErrorMessage(error, 'Erreur lors du chargement de la page'));
    } finally {
      setTabLoading(false);
    }
  };

  // Validation handlers
  const approve = async (requestId: string, side: ValidationSide): Promise<void> => {
    try {
      setValidatingId(requestId);
      if (side === 'finance') {
        await validateFinance(requestId, { approved: true });
      } else {
        await validateAcademic(requestId, { approved: true });
      }
      showSuccess('Demande approuvée avec succès');
      await loadAllRequests();
    } catch (error) {
      showError(apiErrorMessage(error, 'Erreur lors de la validation'));
    } finally {
      setValidatingId(null);
    }
  };

  /** Approving the academic step of a flagged réussite asks for an explicit confirmation. */
  const handleApprove = (request: StudentRequest, side: ValidationSide): void => {
    const needsConfirmation =
      side === 'academic' &&
      request.requestType === AttestationType.ATTESTATION_REUSSITE &&
      !!request.academicAttention;
    if (needsConfirmation) {
      setAttentionConfirm({ request, side });
      return;
    }
    void approve(request.id, side);
  };

  const handleAttentionConfirm = async (): Promise<void> => {
    if (!attentionConfirm) return;
    const { request, side } = attentionConfirm;
    setAttentionConfirm(null);
    await approve(request.id, side);
  };

  const handleReject = async () => {
    if (!rejectionModal || !rejectionReason.trim()) {
      showError('Veuillez fournir une raison de rejet');
      return;
    }

    try {
      setRejectLoading(true);
      const dto = { approved: false, rejectionReason: rejectionReason.trim() };
      if (rejectionModal.type === 'finance') {
        await validateFinance(rejectionModal.requestId, dto);
      } else {
        await validateAcademic(rejectionModal.requestId, dto);
      }
      showSuccess('Demande rejetée');
      await loadAllRequests();
      setRejectionModal(null);
      setRejectionReason('');
    } catch (error) {
      showError(apiErrorMessage(error, 'Erreur lors du rejet'));
    } finally {
      setRejectLoading(false);
    }
  };

  // Physical workflow handlers
  const handleMarkReady = (requestId: string) => {
    setConfirmDialog({
      title: 'Prête à collecter',
      message: 'Marquer cette demande comme prête à être collectée par l\'étudiant ?',
      confirmText: 'Oui, prête',
      variant: 'ready',
      onConfirm: async () => {
        await markAsReadyToCollect(requestId);
        showSuccess('Demande marquée comme prête à collecter');
        await loadAllRequests();
      },
    });
  };

  const handleMarkPrinted = (requestId: string) => {
    setConfirmDialog({
      title: 'Document imprimé',
      message: 'Confirmer que le document a été imprimé et signé ?',
      confirmText: 'Oui, imprimé',
      variant: 'print',
      onConfirm: async () => {
        await markAsPrinted(requestId);
        showSuccess('Document marqué comme imprimé');
        await loadAllRequests();
      },
    });
  };

  const handleDownload = async (request: StudentRequest) => {
    try {
      const { blob, filename } = await downloadForPrint(request.id, request.referenceNumber);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      await loadAllRequests();
    } catch (error) {
      showError(apiErrorMessage(error, 'Erreur lors du téléchargement'));
    }
  };

  const handleDeliverClick = (request: StudentRequest) => {
    setDeliveryNotes('');
    setDeliveryModal({ request });
  };

  const handleDeliverSubmit = async () => {
    if (!deliveryModal) return;

    try {
      setDeliverLoading(true);
      await markAsDelivered(deliveryModal.request.id, deliveryNotes);
      showSuccess('Attestation marquée comme délivrée');
      setDeliveryModal(null);
      setDeliveryNotes('');
      await loadAllRequests();
    } catch (error) {
      showError(apiErrorMessage(error, 'Erreur lors de la livraison'));
    } finally {
      setDeliverLoading(false);
    }
  };

  const handleConfirmDialogSubmit = async () => {
    if (!confirmDialog) return;
    try {
      setConfirmLoading(true);
      await confirmDialog.onConfirm();
      setConfirmDialog(null);
    } catch (error) {
      showError(apiErrorMessage(error, 'Une erreur est survenue'));
    } finally {
      setConfirmLoading(false);
    }
  };

  const currentList = lists[activeTab];
  const currentMeta: PaginationMeta = currentList.meta;

  const filteredRequests = currentList.data.filter((req) => {
    if (activeTab === 'approved') {
      const allowedStatuses = [RequestStatus.APPROVED];

      if (includeReadyToCollect) {
        allowedStatuses.push(
          RequestStatus.GENERATED,
          RequestStatus.DOWNLOADED_FOR_PRINT,
          RequestStatus.PRINTED
        );
      }

      if (includeDelivered) {
        allowedStatuses.push(RequestStatus.DELIVERED);
      }

      if (!allowedStatuses.includes(req.status)) {
        return false;
      }
    }

    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      req.student?.firstName?.toLowerCase().includes(search) ||
      req.student?.lastName?.toLowerCase().includes(search) ||
      req.referenceNumber.toLowerCase().includes(search)
    );
  });

  const tabMeta: Record<TabType, { label: string; Icon: typeof FileText }> = {
    all: { label: 'Toutes les Demandes', Icon: FileText },
    'pending-finance': { label: 'Validation Finance', Icon: DollarSign },
    'pending-academic': { label: 'Validation Académique', Icon: GraduationCap },
    approved: { label: 'Approuvées & Documents', Icon: CheckCircle },
  };

  const downloadButton = (request: StudentRequest, title: string) => (
    <button
      onClick={() => handleDownload(request)}
      className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition"
      title={title}
    >
      <Download className="w-3 h-3" />
      Télécharger
    </button>
  );

  if (loading && currentList.data.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 mt-4">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="w-8 h-8" />
            Gestion des Attestations
          </h1>
          <p className="text-gray-600 mt-1">
            Valider, télécharger, imprimer et délivrer les attestations
          </p>
        </div>
        {canConfigure && (
          <button
            type="button"
            onClick={() => setConfigOpen(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
          >
            <Settings className="w-4 h-4" />
            Configuration des attestations
          </button>
        )}
      </div>

      {/* Year selector — the lists are scoped to one academic year */}
      <div className="mb-4 flex items-center gap-3">
        <Calendar className="w-5 h-5 text-gray-500" />
        <label htmlFor="request-year" className="text-sm font-medium text-gray-700">
          Année académique
        </label>
        <select
          id="request-year"
          value={yearFilter ?? ''}
          onChange={(e) => setYearFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Toutes les années</option>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
              {y.id === currentYearId ? ' (En cours)' : ''}
            </option>
          ))}
        </select>
      </div>

      {yearFilter ? (
        <PendingOtherYearsBanner count={pendingOtherYears} onShowAllYears={() => setYearFilter('')} />
      ) : null}

      {/* Tabs */}
      <div className="mb-6 flex gap-2 border-b border-gray-200 overflow-x-auto">
        {visibleTabs.map((tab) => {
          const { label, Icon } = tabMeta[tab];
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 font-medium transition border-b-2 whitespace-nowrap ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5" />
                {label} ({lists[tab].meta.total})
              </div>
            </button>
          );
        })}
      </div>

      {/* Search & Filters */}
      {(activeTab === 'all' || activeTab === 'approved') && (
        <div className="mb-6 flex items-center gap-4">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom ou référence (page affichée)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {activeTab === 'approved' && (
            <>
              <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={includeReadyToCollect}
                  onChange={(e) => setIncludeReadyToCollect(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Inclure aussi prêtes à collecter</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={includeDelivered}
                  onChange={(e) => setIncludeDelivered(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Inclure aussi les délivrées</span>
              </label>
            </>
          )}
        </div>
      )}

      {/* Content */}
      {filteredRequests.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune demande trouvée</h3>
          <p className="text-gray-600">
            {searchTerm
              ? 'Essayez de modifier votre recherche'
              : yearFilter
                ? "Aucune demande pour cette année académique — changez d'année pour voir l'historique"
                : 'Toutes les demandes ont été traitées'}
          </p>
        </div>
      ) : activeTab === 'all' ? (
        /* All requests view (for coordination) */
        <div className="space-y-4">
          {filteredRequests.map((request) => {
            const badge = getStatusBadge(request.status);
            return (
              <div
                key={request.id}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          {request.student?.firstName} {request.student?.lastName}
                          <AcademicAttentionBadge attention={request.academicAttention} requestType={request.requestType} />
                        </h3>
                        <p className="text-sm text-gray-500">
                          {request.student?.campus?.name}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FileText className="w-4 h-4" />
                        <span>{getTypeLabel(request.requestType)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>{request.academicYear?.name}</span>
                      </div>
                    </div>

                    <div className="text-sm text-gray-500 mb-3">
                      <strong>Référence:</strong> {request.referenceNumber}
                    </div>

                    {/* Validation Status Indicators */}
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      {/* Finance Validation */}
                      {request.financeValidationRequired && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-600">Finance:</span>
                          {request.financeValidatedAt ? (
                            <span className="text-sm font-medium text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              Validée
                            </span>
                          ) : request.status === RequestStatus.REJECTED_FINANCE ? (
                            <span className="text-sm font-medium text-red-600 flex items-center gap-1">
                              <XCircle className="w-4 h-4" />
                              Rejetée
                            </span>
                          ) : (
                            <span className="text-sm font-medium text-yellow-600 flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              En attente
                            </span>
                          )}
                        </div>
                      )}

                      {/* Academic Validation */}
                      {request.academicValidationRequired && (
                        <div className="flex items-center gap-2">
                          <GraduationCap className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-600">Académique:</span>
                          {request.academicValidatedAt ? (
                            <span className="text-sm font-medium text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              Validée
                            </span>
                          ) : request.status === RequestStatus.REJECTED_ACADEMIC ? (
                            <span className="text-sm font-medium text-red-600 flex items-center gap-1">
                              <XCircle className="w-4 h-4" />
                              Rejetée
                            </span>
                          ) : (
                            <span className="text-sm font-medium text-blue-600 flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              En attente
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {request.academicAttention && request.academicAttention.reasons.length > 0 && (
                      <ul className="mt-2 text-xs text-amber-800 list-disc list-inside">
                        {request.academicAttention.reasons.map((reason) => (
                          <li key={reason}>{reason}</li>
                        ))}
                      </ul>
                    )}

                    {request.purpose && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 italic">
                          "{request.purpose}"
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Status Badge */}
                  <div className="ml-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                      {badge.label}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : activeTab === 'pending-finance' || activeTab === 'pending-academic' ? (
        /* Validation view (card-based) */
        <div className="space-y-4">
          {filteredRequests.map((request) => {
            const side: ValidationSide = activeTab === 'pending-finance' ? 'finance' : 'academic';
            return (
              <div
                key={request.id}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          {request.student?.firstName} {request.student?.lastName}
                          <AcademicAttentionBadge attention={request.academicAttention} requestType={request.requestType} />
                        </h3>
                        <p className="text-sm text-gray-500">
                          {request.student?.campus?.name}
                          {request.student?.filiereInscrit ? ` · ${request.student.filiereInscrit}` : ''}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FileText className="w-4 h-4" />
                        <span>{getTypeLabel(request.requestType)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>{request.academicYear?.name}</span>
                      </div>
                    </div>

                    <div className="text-sm text-gray-500">
                      <strong>Référence:</strong> {request.referenceNumber}
                    </div>

                    {request.academicAttention && request.academicAttention.reasons.length > 0 && (
                      <ul className="mt-2 text-xs text-amber-800 list-disc list-inside">
                        {request.academicAttention.reasons.map((reason) => (
                          <li key={reason}>{reason}</li>
                        ))}
                      </ul>
                    )}

                    {request.purpose && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 italic">
                          "{request.purpose}"
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleApprove(request, side)}
                      disabled={validatingId === request.id}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                    >
                      {validatingId === request.id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <CheckCircle className="w-4 h-4" />
                      }
                      Approuver
                    </button>
                    <button
                      onClick={() =>
                        setRejectionModal({
                          requestId: request.id,
                          type: side,
                          studentName: `${request.student?.firstName} ${request.student?.lastName}`,
                        })
                      }
                      disabled={validatingId === request.id}
                      className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition disabled:opacity-50"
                    >
                      <XCircle className="w-4 h-4" />
                      Rejeter
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Document management view (table-based) */
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Référence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Étudiant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Date Demande
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Statut & Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{request.referenceNumber}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-5 h-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            {request.student?.firstName} {request.student?.lastName}
                            <AcademicAttentionBadge attention={request.academicAttention} requestType={request.requestType} />
                          </div>
                          {request.student?.campus && (
                            <div className="text-xs text-gray-500">{request.student.campus.name}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{getTypeLabel(request.requestType)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(request.createdAt).toLocaleDateString('fr-FR')}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex flex-col items-center gap-2">
                        {/* Approuvée : à télécharger pour tamponner */}
                        {request.status === RequestStatus.APPROVED && (
                          <>
                            <span className="text-sm text-gray-500 mb-2">En attente de préparation</span>
                            {canManageRequests && (
                              <div className="flex items-center gap-2">
                                {downloadButton(request, 'Télécharger pour tamponner')}
                                <button
                                  onClick={() => handleMarkReady(request.id)}
                                  className="text-xs text-green-600 hover:text-green-700 font-medium underline"
                                >
                                  &rarr; Prête à collecter
                                </button>
                              </div>
                            )}
                          </>
                        )}

                        {/* Prête à collecter (générée) */}
                        {request.status === RequestStatus.GENERATED && (
                          <>
                            <span className="text-sm font-medium text-green-700 mb-2">Prête à collecter</span>
                            {canManageRequests && (
                              <div className="flex items-center gap-2">
                                {downloadButton(request, 'Télécharger si nécessaire')}
                                <button
                                  onClick={() => handleDeliverClick(request)}
                                  className="text-xs text-green-600 hover:text-green-700 font-medium underline"
                                >
                                  &rarr; Délivrée
                                </button>
                              </div>
                            )}
                          </>
                        )}

                        {/* Téléchargée pour impression */}
                        {request.status === RequestStatus.DOWNLOADED_FOR_PRINT && (
                          <>
                            <span className="text-sm font-medium text-purple-700 mb-2 flex items-center gap-1">
                              <Download className="w-4 h-4" />
                              Téléchargée pour impression
                            </span>
                            {canManageRequests && (
                              <div className="flex items-center gap-2">
                                {downloadButton(request, 'Retélécharger')}
                                <button
                                  onClick={() => handleMarkPrinted(request.id)}
                                  className="text-xs text-indigo-600 hover:text-indigo-700 font-medium underline flex items-center gap-1"
                                >
                                  <Printer className="w-3 h-3" />
                                  &rarr; Imprimée
                                </button>
                                <button
                                  onClick={() => handleDeliverClick(request)}
                                  className="text-xs text-green-600 hover:text-green-700 font-medium underline"
                                >
                                  &rarr; Délivrée
                                </button>
                              </div>
                            )}
                          </>
                        )}

                        {/* Imprimée */}
                        {request.status === RequestStatus.PRINTED && (
                          <>
                            <span className="text-sm font-medium text-indigo-700 mb-2 flex items-center gap-1">
                              <Printer className="w-4 h-4" />
                              Imprimée
                            </span>
                            {canManageRequests && (
                              <div className="flex items-center gap-2">
                                {downloadButton(request, 'Retélécharger')}
                                <button
                                  onClick={() => handleDeliverClick(request)}
                                  className="text-xs text-green-600 hover:text-green-700 font-medium underline"
                                >
                                  &rarr; Délivrée
                                </button>
                              </div>
                            )}
                          </>
                        )}

                        {/* Délivrée */}
                        {request.status === RequestStatus.DELIVERED && (
                          <>
                            <span className="flex items-center gap-1 text-sm font-medium text-blue-700 mb-2">
                              <CheckCircle className="w-4 h-4" />
                              Délivrée
                            </span>
                            {canManageRequests && downloadButton(request, 'Télécharger pour traçabilité')}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <RequestsPagination
        meta={currentMeta}
        disabled={tabLoading || loading}
        onPageChange={(page) => void changePage(activeTab, page)}
      />

      {/* ── Confirm Dialog (for mark ready / mark printed) ── */}
      <ConfirmDialog
        isOpen={!!confirmDialog}
        title={confirmDialog?.title || ''}
        message={confirmDialog?.message || ''}
        confirmText={confirmDialog?.confirmText || 'Confirmer'}
        variant={confirmDialog?.variant || 'info'}
        loading={confirmLoading}
        onConfirm={handleConfirmDialogSubmit}
        onCancel={() => !confirmLoading && setConfirmDialog(null)}
      />

      {/* ── "Étudiant ajourné" safeguard ── */}
      <CuteConfirmModal
        isOpen={!!attentionConfirm}
        variant="warning"
        size="md"
        title="Étudiant ajourné"
        message={
          attentionConfirm?.request.academicAttention
            ? `Cet étudiant est « ${attentionDecisionLabel(attentionConfirm.request.academicAttention)} » pour l'année ${
                attentionConfirm.request.academicYear?.name ?? ''
              }. Voulez-vous quand même délivrer une attestation de réussite ?`
            : ''
        }
        details={
          attentionConfirm?.request.academicAttention && attentionConfirm.request.academicAttention.reasons.length > 0 ? (
            <ul className="text-sm text-amber-800 list-disc list-inside text-left">
              {attentionConfirm.request.academicAttention.reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : undefined
        }
        confirmLabel="Délivrer quand même"
        cancelLabel="Annuler"
        isLoading={!!attentionConfirm && validatingId === attentionConfirm.request.id}
        onConfirm={() => void handleAttentionConfirm()}
        onCancel={() => setAttentionConfirm(null)}
      />

      {/* ── Configuration ── */}
      <AttestationConfigModal
        isOpen={configOpen}
        onClose={() => setConfigOpen(false)}
        onError={showError}
      />

      {/* ── Rejection Modal ── */}
      {rejectionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-[fadeIn_150ms_ease-out]"
            onClick={!rejectLoading ? () => { setRejectionModal(null); setRejectionReason(''); } : undefined}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-[scaleIn_200ms_ease-out]">
            {/* Icon */}
            <div className="px-6 pt-6 text-center">
              <div className="mx-auto w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-7 h-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Rejeter la demande
              </h3>
              <p className="text-sm text-gray-500">
                Demande de <strong>{rejectionModal.studentName}</strong>
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Raison du rejet <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value.slice(0, 500))}
                placeholder="Indiquez la raison du rejet (visible par l'étudiant)..."
                rows={3}
                maxLength={500}
                autoFocus
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none transition"
              />
              <p className="text-xs text-gray-400 text-right mt-1">{rejectionReason.length}/500</p>
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => { setRejectionModal(null); setRejectionReason(''); }}
                disabled={rejectLoading}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700
                           hover:bg-gray-50 transition-all disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectionReason.trim() || rejectLoading}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 rounded-xl text-sm font-medium text-white
                           transition-all active:scale-[0.98] disabled:opacity-50
                           flex items-center justify-center gap-2"
              >
                {rejectLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Confirmer le rejet
              </button>
            </div>
          </div>

          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes scaleIn {
              from { opacity: 0; transform: scale(0.95) translateY(8px); }
              to { opacity: 1; transform: scale(1) translateY(0); }
            }
          `}</style>
        </div>
      )}

      {/* ── Delivery Modal ── */}
      {deliveryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-[fadeIn_150ms_ease-out]"
            onClick={!deliverLoading ? () => setDeliveryModal(null) : undefined}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-[scaleIn_200ms_ease-out]">
            {/* Icon */}
            <div className="px-6 pt-6 text-center">
              <div className="mx-auto w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Marquer comme délivrée
              </h3>
              <p className="text-sm text-gray-500">
                Attestation pour{' '}
                <strong>
                  {deliveryModal.request.student?.firstName} {deliveryModal.request.student?.lastName}
                </strong>
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes de livraison <span className="text-gray-400 font-normal">(optionnel)</span>
              </label>
              <textarea
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value.slice(0, 500))}
                rows={3}
                maxLength={500}
                placeholder="Ex: Remis en main propre, pièce d'identité vérifiée..."
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none transition"
              />
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setDeliveryModal(null)}
                disabled={deliverLoading}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700
                           hover:bg-gray-50 transition-all disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeliverSubmit}
                disabled={deliverLoading}
                className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 rounded-xl text-sm font-medium text-white
                           transition-all active:scale-[0.98] disabled:opacity-50
                           flex items-center justify-center gap-2"
              >
                {deliverLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Confirmer la livraison
              </button>
            </div>
          </div>

          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes scaleIn {
              from { opacity: 0; transform: scale(0.95) translateY(8px); }
              to { opacity: 1; transform: scale(1) translateY(0); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default RequestManagementPage;
